import { useState, useEffect } from 'react';
// import { useAuth } from './auth-context';
import api from '../src/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Shield, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

interface LoginProps {
  onBackToRegister: () => void;
}

export function Login({ onBackToRegister }: LoginProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [formErrors, setFormErrors] = useState({
    email: '',
    password: ''
  });
  const [csrfToken, setCsrfToken] = useState('');

  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const { data } = await api.get('/csrf-token');
        setCsrfToken(data.token);
      } catch (error) {
        console.error('Failed to fetch CSRF token', error);
        setLoginError('No se pudo verificar la seguridad del formulario. Por favor, recargue la página.');
      }
    };
    fetchCsrfToken();
  }, []);

  const validateForm = () => {
    const errors = {
      email: '',
      password: ''
    };

    if (!formData.email.trim()) {
      errors.email = 'El correo es requerido';
    }

    if (!formData.password.trim()) {
      errors.password = 'La contraseña es requerida';
    }

    setFormErrors(errors);
    return !errors.email && !errors.password;
  };

  const handleInputChange = (field: 'email' | 'password') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    if (value.trim() && formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!csrfToken) {
      setLoginError('Error de seguridad no se pudo verificar el formulario. Por favor, recargue la página.');
      return;
    }

    setIsLoading(true);
    setLoginError('');

    try {
      const response = await api.post("/admin-auth/login", {
        email: formData.email,
        password: formData.password,
      }, {
        timeout: 15000,
        headers: {
          'x-csrf-token': csrfToken, // Ensure lower case as per explicit check
        }
      });

      if (response.status === 200) {
        toast.success("Bienvenido", { description: "Has iniciado sesión correctamente." });

        // Reload to update auth context
        window.location.reload();
      } else {
        setLoginError('Credenciales incorrectas. Inténtalo de nuevo.');
      }
    } catch (error: any) {
      console.error("Login error:", error);
      if (error?.code === 'ECONNABORTED') {
        setLoginError('Timeout al autenticar. Verifique conectividad con LDAP e intente nuevamente.');
        return;
      }
      setLoginError(error.response?.data?.message || 'Credenciales incorrectas. Inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto py-10">
      <div className="w-full">
        <Card className="shadow-2xl border-0 bg-white overflow-hidden">
          <CardHeader className="text-center bg-[#0f2744] text-white border-b-4 border-[#c9a227] py-8">
            <div className="flex items-center justify-center mb-2">
              <Shield className="h-10 w-10 text-[#c9a227]" />
            </div>
            <CardTitle className="text-xl md:text-2xl font-black uppercase tracking-tight">Panel de Administración</CardTitle>
            <p className="text-blue-100 text-sm">
              Acceso restringido para personal autorizado
            </p>
          </CardHeader>

          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-bold text-gray-700">Correo Electrónico</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  className={`bg-white ${formErrors.email ? 'border-red-500' : ''}`}
                  placeholder="Ingresa tu correo electrónico"
                />
                {formErrors.email && (
                  <p className="text-red-500 text-sm">{formErrors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" title="password" className="text-sm font-bold text-gray-700">Contraseña</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleInputChange('password')}
                    className={`bg-white pr-10 ${formErrors.password ? 'border-red-500' : ''}`}
                    placeholder="Ingresa tu contraseña"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-500" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-500" />
                    )}
                  </Button>
                </div>
                {formErrors.password && (
                  <p className="text-red-500 text-sm">{formErrors.password}</p>
                )}
              </div>

              {loginError && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertDescription className="text-red-800">
                    {loginError}
                  </AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#0f2744] hover:bg-[#142d4d] text-white py-6 rounded-lg shadow-lg border-b-[4px] border-[#c9a227] active:border-b-0 active:translate-y-[2px] transition-all font-bold uppercase tracking-wider"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Iniciando sesión...
                  </>
                ) : (
                  'Iniciar Sesión'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Button
                variant="ghost"
                onClick={onBackToRegister}
                className="text-[#0f2744] hover:text-[#1a3a5a] hover:bg-gray-100"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver al Registro
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
